<template>
  <div
    class="candidate-assessment"
  >
    <div
      v-if="loadError"
      class="p-6"
      role="alert"
    >
      <p>{{ loadError }}</p>
      <BaseButton
        class="mt-4"
        @click="loadSession"
      >
        Try again
      </BaseButton>
      <BaseButton
        class="mt-4 ml-4"
        variant="inverse"
        @click="returnToDashboard"
      >
        Back to assessments
      </BaseButton>
    </div>
    <div v-else-if="isExample || sessionReady">
      <p
        v-if="!isExample"
        class="px-6 py-2 text-sm"
        role="status"
        aria-live="polite"
      >
        {{ saveStatus }}
      </p>
      <div
        v-if="submitError"
        data-submit-error
        tabindex="-1"
        class="p-6"
        role="alert"
      >
        <p>{{ submitError }}</p>
        <BaseButton
          v-if="!submitInProgress"
          @click="submitResponsesAndRedirect"
        >
          Retry submission
        </BaseButton>
        <BaseButton
          class="ml-4"
          variant="inverse"
          @click="returnToDashboard"
        >
          Back to assessments
        </BaseButton>
      </div>
      <div
        v-if="saveFailed && !deadlineReached"
        class="px-6 pb-4"
      >
        <BaseButton
          v-if="!saveConflict"
          :loading="saving"
          @click="retryCheckpoint"
        >
          Retry save
        </BaseButton>
        <BaseButton
          v-else
          @click="loadSession"
        >
          Load saved answers
        </BaseButton>
      </div>
      <p
        v-if="deadlineReached"
        class="px-6 py-3 font-medium"
        role="status"
      >
        Time has ended. Only answers saved on the server before the deadline can be included. Retry submission if confirmation does not appear.
      </p>
      <fieldset
        :disabled="submitInProgress || deadlineReached || saveConflict"
        class="min-w-0"
      >
        <legend class="sr-only">
          {{ exam.name }} assessment answers
        </legend>
        <header
          :class="['w-full relative flex items-center p-6 shadow z-10 h-16 lg:h-auto bg-white', isExample ? 'justify-between' : 'justify-start']"
        >
          <AssessmentExampleEndButton
            v-if="isExample"
            :modal-open="endModalOpen"
            :is-showing-answers="showAnswers"
            @open="endModalOpen = true"
            @close="endModalOpen = false"
            @showAnswers="setShowAnswers"
          />

          <div :class="['mx-auto', {'overflow-x-auto max-w-screen-xl pb-4 px-6 xl:px-0' : questionCount > 20}]">
            <ul
              v-if="haveQuestionsLoaded(exam.slug) || isExample"
              class="hidden md:flex"
            >
              <li
                v-for="n in questionCount"
                :key="n"
                class="px-2"
              >
                <button
                  type="button"
                  :aria-label="`Question ${n}${hastheQuestionBeenAnswered(n) ? ', answered' : ', unanswered'}`"
                  :aria-current="questionIndex === n - 1 ? 'step' : null"
                  class="px-2 text-gray-600"
                  :class="questionNumberStyles(n)"
                  @click="handleNavigation(n)"
                >
                  {{ n }}
                </button>
              </li>
            </ul>
          </div>
          <template v-if="isExample ? !timerDisabled : !!sessionDeadline">
            <Timer
              v-if="isExample ? exam.isTimed : !!sessionDeadline"
              :class="{'md:self-start ml-4': !isExample}"
              :time-in-minutes="Number(exam.minutes)"
              :deadline-at="sessionDeadline"
              :server-offset="serverOffset"
              :stop="submitInProgress"
              @time-is-up="timeIsUp"
            />
          </template>
        </header>

        <h2
          ref="questionHeading"
          tabindex="-1"
          class="sr-only"
        >
          {{ typeDrives ? 'Work drives' : `Question section ${questionIndex + 1} of ${questionCount}` }}
        </h2>
        <AssessmentDrives
          v-if="typeDrives"
          :is-example="isExample"
          :submit-in-progress="submitInProgress"
          @endTest="submitResponsesAndRedirect()"
        />

        <AssessmentPersonality
          v-if="typePersonality"
          :is-example="isExample"
          :page-number="questionIndex"
          :next-text="nextText"
          :question-preface="exam.questionPreface"
          :submit-error="submitError"
          :submit-in-progress="submitInProgress"
          :personality-traits="exam.personalityTraits"
          :is-last-question="isLastQuestion"
          :exam-slug="exam.slug"
          @endTest="submitResponsesAndRedirect()"
          @prev="prev()"
          @next="next()"
        />

        <div
          v-for="(question, index) in filteredQuestions(exam.slug)"
          :key="index"
        >
          <div
            v-if="index === questionIndex"
            class="bg-white"
          >
            <section
              v-if="typeChoice || typeLikely"
              class="lg:flex"
            >
              <div class="sticky w-full p-6 bg-gray-100 lg:w-2/3 lg:h-screen lg:py-12 lg:px-16">
                <template v-if="typeChoice && !questionHasAsset(question)">
                  <h1 class="text-4xl font-medium">
                    Question {{ index + 1 }}
                  </h1>
                  <p
                    class="mt-6 text-lg prose"
                    v-html="question.bodyHtml"
                  >
                  </p>
                </template>

                <img
                  v-if="question.image_url"
                  :class="questionImageClasses(question, exam)"
                  :src="question.image_url"
                  alt="Question Image"
                />

                <div
                  v-if="question.articleHtml"
                  class="mb-6 markdown"
                  v-html="question.articleHtml"
                />

                <div
                  v-if="question.graphs && question.graphs.length"
                  class="p-6 bg-white"
                >
                  <AssessmentGraph
                    v-for="(graph, index) in question.graphs"
                    :key="index"
                    :graph-data="graph"
                  />
                </div>

                <div
                  v-if="question.table"
                  :class="['markdown overflow-x-scroll md:overflow-x-auto bg-white p-6', markdownTableRowStyling(question.table)]"
                  v-html="marked(question.table)"
                />

                <div v-if="typeLikely">
                  <div
                    v-if="!question.articleHtml"
                    class="px-4 py-2 mb-6 text-sm bg-white border-l-4 border-red-600"
                  >
                    <span>
                      Select both the <strong>most effective</strong> and <strong>least effective</strong> actions to take in response to the situation. Please make sure you’ve selected these <strong>two answers</strong> before moving on.
                    </span>
                  </div>

                  <div
                    class="text-lg text-gray-800"
                    v-html="question.bodyHtml"
                  />
                </div>
              </div>
              <div
                class="w-full p-6 overflow-y-scroll lg:w-1/3 lg:h-screen lg:py-12 lg:px-16 lg:shadow-left"
              >
                <div class="h-1 mb-6 bg-gray-200">
                  <div
                    class="h-1 bg-secondary"
                    :style="{ 'width': progress + '%'}"
                  />
                </div>

                <h1
                  v-if="(typeChoice && questionHasAsset(question) ) || typeLikely"
                  class="text-4xl font-medium"
                >
                  Question {{ index + 1 }}
                </h1>

                <!-- COMPONENTIZE -->
                <template v-if="typeChoice">
                  <p
                    v-if="questionHasAsset(question)"
                    class="mt-6 text-lg"
                    v-html="question.bodyHtml"
                  >
                  </p>

                  <ul class="mt-4">
                    <li
                      v-for="(answer, answerIndex) in question.answers"
                      :key="answerIndex"
                      class="py-2 mt-2 border-b border-gray-300 last:border-b-0"
                    >
                      <label class="inline-flex items-center">
                        <input
                          v-model="choiceResponses[questionIndex]"
                          type="radio"
                          :class="['form-radio text-secondary h-6 w-6', {'cursor-not-allowed': showAnswers}]"
                          :name="index + 1"
                          :value="answer.uuid"
                          :disabled="showAnswers"
                          @change="handleAnswer(answer.uuid, question.uuid)"
                        />
                        <span
                          class="ml-2"
                          v-html="answer.bodyHtml"
                        ></span>
                      </label>
                    </li>
                  </ul>
                </template>
                <!-- END COMPONENTIZE -->

                <!-- COMPONENTIZE -->
                <ul
                  v-if="typeLikely"
                  class="mt-4"
                >
                  <li
                    v-for="(answer, answerIndex) in question.answers"
                    :key="answerIndex"
                    class="pb-4 mb-8 border-b border-gray-300"
                  >
                    <p v-html="answer.bodyHtml"></p>

                    <label
                      v-if="likelyResponses[questionIndex]"
                      class="inline-flex items-center mt-4 mr-8"
                    >
                      <input
                        v-model="likelyResponses[questionIndex]['most_likely']"
                        type="radio"
                        :class="['form-radio text-secondary h-6 w-6', {'cursor-not-allowed': showAnswers}]"
                        :name="`likely-most-${question.uuid}`"
                        :aria-label="`Most effective: ${answer.body || answer.bodyHtml.replace(/<[^>]*>/g, '')}`"
                        :value="answer.uuid"
                        :disabled="showAnswers"
                        @change="handleLikelyAnswer(questionIndex, question.uuid, 'most')"
                      />
                      <span class="ml-2">Most</span>
                    </label>

                    <label
                      v-if="likelyResponses[questionIndex]"
                      class="inline-flex items-center mt-4"
                    >
                      <input
                        v-model="likelyResponses[questionIndex]['least_likely']"
                        type="radio"
                        :class="['form-radio text-secondary h-6 w-6', {'cursor-not-allowed': showAnswers}]"
                        :name="`likely-least-${question.uuid}`"
                        :aria-label="`Least effective: ${answer.body || answer.bodyHtml.replace(/<[^>]*>/g, '')}`"
                        :value="answer.uuid"
                        :disabled="showAnswers"
                        @change="handleLikelyAnswer(questionIndex, question.uuid, 'least')"
                      />
                      <span class="ml-2">Least</span>
                    </label>
                  </li>
                </ul>
                <!-- END COMPONENTIZE -->

                <div class="inline-flex my-8">
                  <BaseButton
                    class="mr-4"
                    :disabled="questionIndex < 1"
                    variant="inverse"
                    @click="prev"
                  >
                    Back
                  </BaseButton>
                  <BaseButton
                    v-if="!isLastQuestion"
                    :disabled="!canPressNext"
                    :loading="submitInProgress"
                    @click="next()"
                  >
                    Next
                  </BaseButton>
                  <AssessmentEndButton
                    v-if="isLastQuestion && !isExample"
                    :submit-error="submitError"
                    :submit-in-progress="submitInProgress"
                    button-type="button"
                    button-text="Finish"
                    @endTest="submitResponsesAndRedirect()"
                    @examCancelledAfterError="returnToDashboard"
                  />

                  <AssessmentExampleEndButton
                    v-if="isLastQuestion && isExample"
                    :modal-open="endModalOpen"
                    :is-showing-answers="showAnswers"
                    @open="endModalOpen = true"
                    @close="endModalOpen = false"
                    @showAnswers="setShowAnswers"
                  >
                    <template v-slot="{ isShowingAnswers }">
                      <BaseButton
                        v-if="!isShowingAnswers"
                        @click="completedModalOpen = true"
                      >
                        Finish
                      </BaseButton>
                    </template>
                  </AssessmentExampleEndButton>
                </div>

                <div
                  v-if="isExample && showAnswers"
                  class="mt-8 text-gray-600"
                  v-html="question.explanation"
                >
                </div>
              <!-- END COMPONENTIZE -->
              </div>
            </section>
          </div>
        </div>
      </fieldset>
    </div>
    <div
      v-else
      class="my-12"
    >
      <Loader />
    </div>

    <template v-if="!timerDisabled">
      <AssessmentTimeUpModal
        v-if="!isExample"
        :modal-open="timeUpModalOpen"
        :assessments-dashboard-route="assessmentsDashboardRoute"
        @close="timeUpModalOpen = false"
      />
    </template>

    <AssessmentFeedbackModal
      v-if="!isExample"
      :job-id="jobUuid"
      :modal-open="feedbackModalOpen"
      @close="closeFeedbackModal"
    />

    <AssessmentsCompleteModal
      v-if="!isExample"
      :modal-open="assessmentsCompleteModalOpen"
      @close="$router.push({ name: 'candidate-wizard-dashboard' })"
    />

    <AssessmentExampleCompleteModal
      v-if="isExample"
      :instruction-slug="exam.instruction_slug"
      :modal-open="completedModalOpen"
      @close="completedModalOpen = false"
      @showAnswers="setShowAnswers"
    />
  </div>
</template>

<script>
import AssessmentTimeUpModal from '@components/Assessments/AssessmentTimeUpModal'
import AssessmentEndButton from '@components/Assessments/AssessmentEndButton'
import AssessmentFeedbackModal from '@components/Assessments/AssessmentFeedbackModal'
import AssessmentsCompleteModal from '@components/Assessments/AssessmentsCompleteModal'

import AssessmentGraph from '@components/Assessments/AssessmentGraph'

import AssessmentExampleEndButton from '@components/Assessments/AssessmentExampleEndButton'
import AssessmentExampleCompleteModal from '@components/Assessments/AssessmentExampleCompleteModal'

import Timer from '@components/Timer'
import Loader from '@components/Loader'

import AssessmentDrives from '@components/Assessments/AssessmentDrives'

import AssessmentPersonality from '@components/Assessments/AssessmentPersonality'

import marked from 'marked'
import { mapGetters } from 'vuex'
import { AssessmentSession } from '@api/assessmentSession'
import { sameResponses } from '@utils/assessmentRecovery'

export default {
  components: {
    AssessmentExampleEndButton,
    AssessmentExampleCompleteModal,
    AssessmentPersonality,
    AssessmentGraph,
    AssessmentDrives,
    AssessmentTimeUpModal,
    AssessmentEndButton,
    Timer,
    Loader,
    AssessmentFeedbackModal,
    AssessmentsCompleteModal
  },

  props: {
    exam: {
      type: Object,
      default: null
    },
    candidate: {
      type: Object,
      default: null
    },
    isExample: {
      type: Boolean,
      default: false
    },
    exampleQuestions: {
      type: Array,
      default: () => []
    },
    jobUuid: {
      type: String,
      default: null
    },
    jobWizardSlug: {
      type: String,
      default: null
    },
    jobExams: {
      type: Array,
      default: null
    },
    organisationId: {
      type: String,
      default: null
    },
    antiCheatData: {
      type: Object,
      default: null
    },
    timerDisabled: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      marked,
      sessionReady: false,
      sessionDeadline: null,
      serverOffset: 0,
      loadError: '',
      saveStatus: 'Your answers are saved as you go.',
      saving: false,
      saveFailed: false,
      saveConflict: false,
      deadlineReached: false,
      retryCount: 0,
      localRecoveryAvailable: true,
      destroyed: false,
      loadGeneration: 0,
      checkpointTimer: null,
      unsubscribeProgress: null,

      endModalOpen: false,
      timeUpModalOpen: false,
      completedModalOpen: false,
      feedbackModalOpen: false,
      assessmentsCompleteModalOpen: false,

      showAnswers: false,

      questionIndex: 0,
      choiceResponses: [],
      likelyResponses: [],

      submitInProgress: false,
      submitError: '',

      attemptExamSlugs: []
    }
  },

  computed: {
    ...mapGetters({
      haveQuestionsLoaded: 'exams/haveQuestionsLoaded',
      haveExamsLoaded: 'exams/haveExamsLoaded',
      storedQuestions: 'exams/questions',
      examBySlug: 'exams/examBySlug',

      candidateId: 'candidates/candidateId',
      token: 'candidates/token',
      candidateSittingToken: 'candidates/candidateSittingToken',
      jobId: 'candidates/jobId',
      extraTime: 'attempt/extraTime',

      personalityDataPages: 'personality/totalPages'
    }),

    /**
     * @return {Array}
     */
    questions() {
      return this.exampleQuestions.length ? this.exampleQuestions : this.storedQuestions(this.exam.slug)
    },

    /**
     * @return {String}
     */
    progress() {
      return (this.questionIndex / this.questionCount) * 100
    },

    /**
     * @return {Boolean}
     */
    typeLikely() {
      return this.exam.instruction_slug === 'likely'
    },

    /**
     * @return {Boolean}
     */
    typeChoice() {
      return this.exam.instruction_slug === 'choice'
    },

    /**
     * @return {Boolean}
     */
    typePersonality() {
      return this.exam.instruction_slug === 'personality'
    },

    /**
     * @return {Boolean}
     */
    typeDrives() {
      return this.exam.instruction_slug === 'drives'
    },

    /**
     * @return {Number}
     */
    questionCount() {
      if (this.typePersonality && this.exam.slug) {
        return this.personalityDataPages(this.exam.personalityTraits, this.exam.slug)
      }

      if (this.typeDrives) {
        return
      }

      if (!this.isExample && !this.haveQuestionsLoaded(this.exam.slug)) {
        return
      }

      return this.questions.length
    },

    /**
     * @return {Boolean}
     */
    isLastQuestion() {
      return !!(this.questionIndex === this.questionCount - 1)
    },

    /**
     * @return {String}
     */
    nextText() {
      return this.isLastQuestion ? 'Finish' : 'Next'
    },

    /**
     * Can the user press the next button?
     *
     * Mostly used for likely-type questions.
     *
     * @return {Boolean}
     */
    canPressNext() {
      if (!this.typeLikely) {
        return true
      }

      const question = this.likelyResponses[this.questionIndex]

      if (!question) {
        return false
      }

      if (question['least_likely'] === question['most_likely']) {
        return false
      }

      return question['least_likely'] && question['most_likely']
    },

    /**
     * For use with a nuxt-link to
     *
     * @return {Object}
     */
    assessmentsDashboardRoute() {
      if (this.$usesFeature('classic-jobs')) {
        return {
          name: 'assessments',
          query: {
            candidate: this.candidateId,
            job: this.jobId,
            token: this.token,
            sitting: this.candidateSittingToken
          }
        }
      }

      if (this.$usesFeature('candidate-wizard')) {
        return {
          name: 'candidate-wizard-job', params: { job: this.jobWizardSlug }
        }
      }
    },

    /**
     * @return {Boolean}
     */
    showEndTestButton() {
      return !this.isExample && !this.typePersonality
    },

    /**
     * @return {Boolean}
     */
    candidateWizardJobId() {
      if (!this.$usesFeature('candidate-wizard')) {
        return
      }
      return this.jobUuid
    },

    /**
     * @return {Boolean}
     */
    isLastCoreExam() {
      return this.jobExams && this.jobExams.length === this.attemptExamSlugs.length
    }
  },

  created() {
    if (this.isExample) { this.restart() } else { this.loadSession() }
    this.getAttemptExamSlugs()
  },

  mounted() {
    window.addEventListener('online', this.retryCheckpoint)
    window.addEventListener('beforeunload', this.warnUnsaved)
    document.addEventListener('visibilitychange', this.resumeFromBackground)
  },

  beforeDestroy() {
    this.destroyed = true
    this.loadGeneration++
    if (this.assessmentSession) { this.assessmentSession.dispose() }
    window.removeEventListener('online', this.retryCheckpoint)
    window.removeEventListener('beforeunload', this.warnUnsaved)
    document.removeEventListener('visibilitychange', this.resumeFromBackground)
    clearTimeout(this.checkpointTimer)
    if (this.unsubscribeProgress) { this.unsubscribeProgress() }
  },

  methods: {
    async loadSession() {
      const generation = ++this.loadGeneration
      clearTimeout(this.checkpointTimer)
      if (this.assessmentSession) { this.assessmentSession.dispose() }
      this.loadError = ''
      this.saveConflict = false
      this.saveFailed = false
      this.deadlineReached = false
      this.sessionReady = false
      if (this.unsubscribeProgress) { this.unsubscribeProgress(); this.unsubscribeProgress = null }
      try {
        this.assessmentSession = new AssessmentSession(this.candidateWizardJobId)
        const data = await this.assessmentSession.start(this.exam, this.extraTime)
        if (generation !== this.loadGeneration || this.destroyed) { return }
        if (data.completed) { this.returnToDashboard(); return }
        this.$store.commit('exams/questions', { examSlug: this.exam.slug, questions: data.questions })
        this.$store.commit('personality/statements', { examSlug: this.exam.slug, statements: data.statements })
        this.restart()
        this.$store.commit('personality/restoreAnswers', this.typePersonality ? data.responses : {})
        this.$store.commit('drives/setResults', this.typeDrives ? data.responses : {})
        if (this.typeChoice || this.typeLikely) {
          data.responses.forEach(response => {
            this.$store.dispatch('attempt/handleAnswer', response)
            const index = data.questions.findIndex(q => q.uuid === response.questionUuid)
            if (this.typeChoice) { this.$set(this.choiceResponses, index, response.answer.uuid) } else { this.$set(this.likelyResponses, index, { most_likely: response.answer.likely.most, least_likely: response.answer.likely.least }) }
          })
        }
        this.sessionDeadline = data.deadlineAt
        this.serverOffset = Date.parse(data.serverNow) - Date.now()
        this.saveStatus = this.assessmentSession.recovered ? 'Restored unsaved answers from this tab. Reconnecting to save them.'
          : this.assessmentSession.recoveryConflict ? 'Progress changed elsewhere. The latest server-saved answers have been loaded.' : 'Your answers have been saved.'
        this.sessionReady = true
        this.$nextTick(this.focusQuestion)
        if (this.assessmentSession.recovered) { this.scheduleCheckpoint() }
        this.unsubscribeProgress = this.$store.subscribe(mutation => {
          if (['attempt/handleAnswer', 'attempt/setAnswer', 'personality/setAnswer', 'drives/setResults'].includes(mutation.type)) {
            this.scheduleCheckpoint()
          }
        })
      } catch (error) {
        if (generation !== this.loadGeneration || this.destroyed) { return }
        this.loadError = (error.response && error.response.data && error.response.data.message) || 'We could not load your assessment. Please try again.'
      }
    },

    responses() {
      if (this.typePersonality) { return this.$store.state.personality.answers }
      if (this.typeDrives) { return this.$store.state.drives.result }
      return this.$store.state.attempt.answers
    },

    scheduleCheckpoint() {
      if (this.deadlineReached || this.saveConflict || this.destroyed) { return }
      clearTimeout(this.checkpointTimer)
      this.localRecoveryAvailable = this.assessmentSession.remember(this.responses())
      this.saveStatus = 'Saving your answers…'
      this.checkpointTimer = setTimeout(this.saveCheckpoint, 300)
    },

    async saveCheckpoint() {
      if (!this.sessionReady || this.submitInProgress || this.deadlineReached || this.saveConflict || this.destroyed) { return }
      // One active request; changes made while it is in flight are saved on its acknowledgement.
      if (this.saving) { return }
      this.saving = true
      const snapshot = JSON.parse(JSON.stringify(this.responses()))
      try {
        const data = await this.assessmentSession.checkpoint(snapshot)
        if (this.destroyed) { return }
        this.serverOffset = Date.parse(data.serverNow) - Date.now()
        this.saveFailed = false
        this.retryCount = 0
        this.saveStatus = 'Your answers have been saved.'
        if (data.completed) { this.returnToDashboard(); return }
        if (this.assessmentSession.expired()) { this.timeIsUp(); return }
        if (!sameResponses(snapshot, this.responses())) { this.checkpointTimer = setTimeout(this.saveCheckpoint, 0) }
      } catch (error) {
        if (this.destroyed) { return }
        this.saveFailed = true
        this.saveConflict = !!(error.response && error.response.status === 409)
        const status = error.response && error.response.status
        this.saveStatus = this.saveConflict ? 'Progress changed in another tab. Load the saved answers before continuing.'
          : status === 401 || status === 403 ? 'Your invitation could not be verified. Reopen your invitation to continue. Answers are not confirmed saved.'
            : 'Your latest answers are not saved on the server. ' + (this.localRecoveryAvailable ? 'Recovery in this tab expires after 24 hours. ' : 'Keep this page open. ') + 'Check your connection and retry before time runs out.'
        if ((!status || status >= 500 || status === 429) && this.retryCount < 4) {
          this.checkpointTimer = setTimeout(this.saveCheckpoint, [2000, 5000, 10000, 30000][this.retryCount++])
        }
      } finally { this.saving = false }
    },

    retryCheckpoint() {
      if (this.submitError && !this.saveConflict) { this.submitResponsesAndRedirect(); return }
      this.retryCount = 0
      clearTimeout(this.checkpointTimer)
      this.saveCheckpoint()
    },

    resumeFromBackground() {
      if (document.visibilityState === 'visible') { this.retryCheckpoint() }
    },

    warnUnsaved(event) {
      if (!this.isExample && this.sessionReady && (this.saving || this.saveFailed || !sameResponses(this.responses(), this.assessmentSession.data.responses))) {
        event.preventDefault()
        event.returnValue = ''
      }
    },

    focusQuestion() {
      const heading = this.$refs.questionHeading
      if (heading) { heading.focus() }
    },

    restart() {
      this.questionIndex = 0

      // Creates response arrays ready to be filled by the user
      this.choiceResponses = Array(this.questionCount).fill(null)
      this.likelyResponses = Array.from({ length: this.questionCount }, () => ({ most_likely: null, least_likely: null }))

      // Clears existing answers
      this.$store.commit('attempt/clearAnswers')
    },

    handleNavigation(index) {
      this.questionIndex = index - 1
      this.$nextTick(this.focusQuestion)
    },

    filteredQuestions(examSlug) {
      if (examSlug === 'drives') {
        return this.drives
      } else {
        return this.questions
      }
    },

    /**
     * Take the user to the next question, or submit response if last
     */
    next() {
      if (this.submitInProgress) {
        return
      }

      if (this.questionIndex + 1 < this.questionCount) {
        this.questionIndex++
        this.$nextTick(this.focusQuestion)
      }
    },

    prev() {
      if (this.questionIndex > 0) {
        this.questionIndex--
        this.$nextTick(this.focusQuestion)
      }
    },

    questionNumberStyles(index) {
      return [
        { 'inline-blo k border-b-2 border-secondary pb-1 font-semibold leading-none text-gray-800': this.questionIndex === index - 1 },
        { 'text-secondary': this.hastheQuestionBeenAnswered(index) }
      ]
    },

    hastheQuestionBeenAnswered(index) {
      // Has the choice question been answered?
      if (this.typeChoice) {
        return this.choiceResponses[index - 1] !== null
      }

      // Has the likely question been answered?
      if (this.typeLikely) {
        if (!this.likelyResponses[index - 1]) {
          return false
        }
        return Object.values(this.likelyResponses[index - 1])
          .every(response => {
            return response !== null
          })
      }

      // // Has the personality question been answered?
      // if (this.typePersonality) {
      //   return true
      // }
    },

    setShowAnswers() {
      this.endModalOpen = false
      this.completedModalOpen = false
      this.showAnswers = true
      this.questionIndex = 0

      // Set correct Choice answers for Example questions
      if (this.typeChoice) {
        const correctAnswers = this.questions.map(question => {
          return question.answers.find(answer => answer.correct)
        })
        this.choiceResponses = correctAnswers.map(correctAnswer => correctAnswer.uuid)
      }

      // Set correct Likely answers for Example questions
      if (this.typeLikely) {
        const mostLikely = this.questions.map(question => {
          return question.answers.find(answer => answer.mostLikely)
        })
        const leastLikely = this.questions.map(question => {
          return question.answers.find(answer => answer.leastLikely)
        })

        this.likelyResponses = mostLikely.map((likely, i) => {
          return (
            {
              most_likely: likely.uuid,
              least_likely: leastLikely[i].uuid
            }
          )
        })
      }
    },

    /**
     * @param {string} answerUuid
     * @param {string} questionUuid
     */
    handleAnswer(answerUuid, questionUuid) {
      this.$store.dispatch('attempt/handleAnswer', {
        answer: { uuid: answerUuid }, questionUuid
      })
    },

    handleLikelyAnswer(questionIndex, questionUuid, changed) {
      const selected = this.likelyResponses[questionIndex]
      if (selected.most_likely && selected.most_likely === selected.least_likely) {
        this.$set(selected, changed === 'most' ? 'least_likely' : 'most_likely', null)
      }
      this.$store.dispatch('attempt/handleAnswer', {
        answer: {
          likely: {
            most: this.likelyResponses[questionIndex]['most_likely'],
            least: this.likelyResponses[questionIndex]['least_likely']
          }
        },
        questionUuid
      })
    },

    timeIsUp() {
      if (this.isExample || this.deadlineReached) { return }
      this.deadlineReached = true
      this.submitResponsesAndRedirect()
    },

    async submitResponses() {
      if (this.isExample) { return }
      clearTimeout(this.checkpointTimer)
      this.submitInProgress = true
      this.submitError = ''
      const result = await this.assessmentSession.complete(this.responses(), this.antiCheatData || {})
      if (this.destroyed) { return result }
      this.$store.commit('candidates/addAttempt', result)
      this.$emit('completed')
      return result
    },

    /**
     * Submit the responses, the redirect if no error
     *
     * @return void
     */
    submitResponsesAndRedirect() {
      if (this.submitInProgress) {
        console.error('Submmission already in progress')
        return
      }

      this.submitInProgress = true

      this.submitResponses()
        .then(() => {
          if (this.destroyed) { return }
          this.submitInProgress = false

          // Handle Feedback and submit to employer
          if (this.$usesFeature('candidate-wizard')) {
            this.attemptExamSlugs.push(this.exam.slug)

            if (this.isLastCoreExam) {
              this.feedbackModalOpen = true
              return
            }
          }

          this.returnToDashboard()
        })
        .catch(error => {
          if (this.destroyed) { return }
          if (error.response && error.response.status === 409) {
            this.saveConflict = true
            this.saveFailed = true
            this.saveStatus = 'Progress changed in another tab. Load the saved answers before continuing.'
          }
          this.submitInProgress = false

          this.$nextTick(() => { const alert = this.$el.querySelector('[data-submit-error]'); if (alert) { alert.focus() } })
          if (!this.submitError) {
            this.submitError = (error.response && error.response.data && error.response.data.message) || 'We could not confirm your submission. Please keep this page open and try again.'
          }
        })
    },

    closeFeedbackModal() {
      this.feedbackModalOpen = false
      this.assessmentsCompleteModalOpen = true
    },

    returnToDashboard() {
      this.$router.push(this.assessmentsDashboardRoute)
    },

    getAttemptExamSlugs() {
      // Used to work out the last exam for Candidate Wizards
      if (this.isExample || this.$usesFeature('classic-jobs')) {
        return []
      }

      if (!this.candidate || !this.candidate.attempts.length) {
        return []
      }
      this.attemptExamSlugs = this.candidate.attempts.map(attempt => attempt.examSlug)
    },

    markdownTableRowStyling(table) {
      // Bold last table row if a "total"
      if (table.includes('Total')) {
        return 'markdown-table'
      }
    },

    /**
     * Decides the CSS classes for an image
     *
     * @param {Object} question
     * @param {Object} exam
     * @return {string}
     */
    questionImageClasses(question, exam) {
      // All images should have auto margins
      const classes = ['mx-auto']

      // If an exam has an organisationId, it’s a CustomExam. Images from
      // CustomExams are allowed to be full-sized as they are managed by
      // Cloudinary
      if (!exam.organisationId && !exam.examSuiteSlug) {
        // Legacy option for older tests sent directly
        classes.push('md:max-w-lg xl:max-w-xl')
      }

      let sizeClass = 'w-full'
      if (question.imageData) {
        // we have imageData, which will tell us the height and width if possible
        const imageData = question.imageData
        if (imageData.height && imageData.width) {
          if (imageData.height > imageData.width) {
            // Image is portrait, so make it half height and hope for the best
            sizeClass = 'h-1/2'
          }
        }
      }
      classes.push(sizeClass)

      return classes
    },

    /**
     * @return {Boolean}
     */
    questionHasAsset(question) {
      if (!question) {
        return false
      }
      return !!question.image_url || !!question.articleHtml || (question.graphs && !!question.graphs.length) || !!question.table
    }
  }
}
</script>

<style>
.article p, .article ul {
  margin-bottom: 1em;
}
</style>
